<template>
  <div>
    <Title :pages="[{ icon: 'sitemap', path: '/' + section, page: 'ORGANIGRAMA' }, { icon: 'circle', page: is_new ? 'Nuevo puesto' : 'Editar puesto' }]"></Title>

    <v-container fluid>
      <v-tabs v-model="tab" background-color="#fff" slider-color="transparent" class="custom-tabs mb-4">
        <v-tab class="custom-tab" :class="{ 'active-tab': tab === 0 }">Puesto</v-tab>
        <v-tab v-if="!is_new" class="custom-tab" :class="{ 'active-tab': tab === 1 }">Dependientes ({{ item.children.length }})</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" style="background: transparent">
        <v-tab-item>
          <v-row>
            <v-col cols="12" lg="3">
              <CardSkeletonList v-if="loading_item" :count="1" />
              <CardPreview v-else :item="item" :parent="parent_node" :children="item.children" />
            </v-col>
            <v-col cols="12" lg="9">
              <CardSkeletonList v-if="loading_item" :count="3" />
              <template v-else>
                <NodeForm
                  ref="NodeForm"
                  :item="item"
                  :nodes="nodes"
                  :node_types="node_types"
                  :sucursales="sucursales"
                  :worksectors="worksectors"
                  :legajos="legajos"
                  :loading_legajos="loading_legajos"
                  :disabled="read_only"
                />
                <CardDependents
                  v-if="!is_new"
                  :parent_id="item.id"
                  :children="item.children"
                  :create_access="create_access"
                  :update_access="update_access"
                  :delete_access="delete_access"
                  @addChild="addChild"
                  @editChild="goToNode"
                  @assignResponsible="assignResponsible"
                  @deleteChild="(child) => confirmDelete(child)"
                  @reordered="loadItem"
                />

                <v-card class="pa-6">
                  <div class="d-flex flex-wrap">
                    <v-btn v-if="!is_new && delete_access" outlined color="error" @click="confirmDelete(item)">Eliminar puesto</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn outlined color="primary" class="mr-3" @click="back">Cancelar</v-btn>
                    <v-btn v-if="!read_only" color="primary" :loading="loading_save" @click="save">Guardar</v-btn>
                  </div>
                </v-card>
              </template>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item v-if="!is_new">
          <CardDependents
            :parent_id="item.id"
            :children="item.children"
            :create_access="create_access"
            :update_access="update_access"
            :delete_access="delete_access"
            @addChild="addChild"
            @editChild="goToNode"
            @assignResponsible="assignResponsible"
            @deleteChild="(child) => confirmDelete(child)"
            @reordered="loadItem"
          />
        </v-tab-item>
      </v-tabs-items>

      <ModalAssignResponsible v-if="update_access" :dialog="dialog_assign" :node="node_assign" @updateDialog="(value) => (dialog_assign = value)" @assigned="loadItem" />
      <DialogDelete :dialog_delete="dialog_delete" :subtext="DELETE_SUBTEXT" @updateDialogDelete="updateDialogDelete" @deleteItemConfirm="deleteItemConfirm" />
    </v-container>
  </div>
</template>

<script>
import Constant from "@/constants/sections";
import { orgChartService } from "@/libs/ws/orgChartService";
import { generalService } from "@/libs/ws/generalService";
import { legajoService } from "@/libs/ws/legajoService";
import GenericMixin from "@/mixins/GenericMixin.vue";
import RolMixin from "@/mixins/RolMixin.vue";
import Title from "@/components/Title.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import CardSkeletonList from "@/components/CardSkeletonList.vue";
import NodeForm from "./OrgChartForm/NodeForm.vue";
import CardPreview from "./OrgChartForm/CardPreview.vue";
import CardDependents from "./OrgChartForm/CardDependents.vue";
import ModalAssignResponsible from "./ModalAssignResponsible.vue";
import { DELETE_SUBTEXT } from "@/constants/org_chart";

const EMPTY_ITEM = {
  id: 0,
  name: "",
  node_type: null,
  external_reference: "",
  description: "",
  enabled: true,
  sort_order: "",
  children_collapsed: false,
  parent: null,
  sucursal: null,
  worksector: null,
  legajo: null,
  substitute: null,
  children: [],
};

export default {
  name: "OrgChartForm",
  components: { Title, DialogDelete, CardSkeletonList, NodeForm, CardPreview, CardDependents, ModalAssignResponsible },
  mixins: [GenericMixin, RolMixin],
  data() {
    return {
      DELETE_SUBTEXT: DELETE_SUBTEXT,
      section: Constant.SECTION_ORG_CHART,
      service: orgChartService,
      tab: 0,
      item: JSON.parse(JSON.stringify(EMPTY_ITEM)),
      // Requeridos por GenericMixin.close()
      mainItem: {},
      defaultItem: {},
      nodes: [],
      node_types: [],
      sucursales: [],
      worksectors: [],
      legajos: [],
      loading_item: false,
      loading_legajos: false,
      loading_save: false,
      dialog_assign: false,
      node_assign: null,
      node_delete: null,
    };
  },
  computed: {
    is_new() {
      return this.$route.params.id == "new";
    },
    read_only() {
      return this.is_new ? !this.create_access : !this.update_access;
    },
    // Puesto superior completo (el item solo trae id y nombre)
    parent_node() {
      if (this.item.parent == null) return null;
      return this.nodes.find((node) => node.id == this.item.parent.id) || this.item.parent;
    },
  },
  watch: {
    // Al navegar entre puestos (ej. editar un dependiente) se reutiliza el componente
    "$route.params.id"() {
      this.tab = 0;
      this.init();
    },
  },
  mounted() {
    this.loadCombos();
    this.init();
  },
  methods: {
    init() {
      this.item = JSON.parse(JSON.stringify(EMPTY_ITEM));
      if (this.is_new) this.loadNodes().then(this.setDefaultsFromParent);
      else {
        this.loadItem();
        this.loadNodes();
      }
    },
    loadCombos() {
      this.loadInto("node_types", orgChartService.list_node_types());
      this.loadInto("sucursales", generalService.filter({ enabled: true }, Constant.SECTION_SUCURSALES));
      this.loadInto("worksectors", generalService.filter({}, Constant.SECTION_WORKSECTORS));
      this.loading_legajos = true;
      this.loadInto("legajos", legajoService.filter({ status: "linked" })).finally(() => (this.loading_legajos = false));
    },
    loadNodes() {
      return this.loadInto("nodes", orgChartService.filter());
    },
    loadInto(key, request) {
      return request
        .then((response) => {
          if (response.status) this[key] = response.result;
        })
        .catch((e) => console.log(e));
    },
    // Alta desde "Agregar dependiente": se precarga el superior y su sucursal / sector
    setDefaultsFromParent() {
      const parent_id = this.$route.query.parent_id;
      const parent = parent_id != null ? this.nodes.find((node) => node.id == parent_id) : null;
      if (parent == null) return;

      this.item.parent = parent;
      this.item.sucursal = parent.sucursal;
      this.item.worksector = parent.worksector;
    },
    loadItem() {
      this.loading_item = this.item.id == 0;
      this.service
        .get(parseInt(this.$route.params.id))
        .then((response) => {
          if (response.status) this.item = response.result;
          else this.$toast.error(response.msg, "Error");
        })
        .catch((e) => console.log(e))
        .finally(() => (this.loading_item = false));
    },
    toRef(object) {
      return object != null && object.id != null ? { id: object.id } : null;
    },
    async save() {
      if (this.loading_save) return;
      const valid = await this.$refs.NodeForm.validate();
      if (!valid) {
        this.$toast.error("Complete los campos obligatorios.", "Error");
        return;
      }

      const data = {
        id: this.item.id,
        name: this.item.name,
        node_type: this.toRef(this.item.node_type),
        external_reference: this.item.external_reference,
        description: this.item.description,
        enabled: this.item.enabled,
        sort_order: this.item.sort_order !== "" && this.item.sort_order != null ? parseInt(this.item.sort_order) : null,
        children_collapsed: this.item.children_collapsed,
        parent: this.toRef(this.item.parent),
        sucursal: this.toRef(this.item.sucursal),
        worksector: this.toRef(this.item.worksector),
        legajo: this.toRef(this.item.legajo),
        substitute: this.toRef(this.item.substitute),
      };

      this.loading_save = true;
      const request = this.is_new ? this.service.create(data) : this.service.update(data);
      request
        .then((response) => {
          if (response.status) {
            this.$toast.success(this.is_new ? "El puesto se creo correctamente." : "El puesto se modifico correctamente.", "OK");
            this.back();
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          console.log(e);
          this.$toast.error("Ocurrio un error al guardar el puesto.", "Error");
        })
        .finally(() => (this.loading_save = false));
    },
    back() {
      this.$router.push("/" + this.section);
    },
    addChild() {
      this.$router.push({ path: "/" + this.section + "/new", query: { parent_id: this.item.id } });
    },
    goToNode(node) {
      this.$router.push("/" + this.section + "/" + node.id);
    },
    assignResponsible(node) {
      this.node_assign = node;
      this.dialog_assign = true;
    },
    confirmDelete(node) {
      this.node_delete = node;
      this.dialog_delete = true;
    },
    deleteItemConfirm() {
      this.dialog_delete = false;
      const deleting_self = this.node_delete.id == this.item.id;
      this.service
        .remove({ id: this.node_delete.id })
        .then((response) => {
          if (response.status) {
            this.$toast.success(response.msg, "OK");
            if (deleting_self) this.back();
            else this.loadItem();
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => console.log(e));
    },
  },
};
</script>
